import type { PreventerGlobal } from '@/mqtt/types';

/**
 * The preventer engine — port of `src/store/plugins/preventerHandler.ts`
 * (docs/MIGRATION.md §6).
 *
 * Two independent jobs, both of which decide whether a wall-mounted display shows live data
 * or a frozen screen:
 *
 * 1. **`initPreventer`** — get a token out of the third-party `preventer.js`. The token is
 *    source of the MQTT login (a UUID; `splitCredentials` in `client.ts` derives the pair).
 * 2. **`startTopicAccessCheck`** — POST the exact topic list to the preventer's `savemeta`
 *    endpoint, which provisions the broker ACL. Nothing may subscribe before it answers
 *    `metaEnabled`.
 *
 * The topic strings sent here and the strings handed to `client.subscribe` come from the
 * same `topicItems` map for a reason: the broker's ACL is provisioned from *these* strings,
 * so any drift between the two lists is a silent, remote subscribe refusal (CLAUDE.md
 * constraint 2). Every step logs with a `[preventer]` prefix — on a deployed display the
 * console is the only diagnostic channel there is.
 *
 * Nothing in this file logs the token or the API key.
 */

/** One entry of the `meta.topics` array `savemeta` expects. */
export type MetaTopic = { name: string; permission: 'read' | 'write' };

/**
 * Used when `VITE_PREVENTER_META_URL` is unset: the HqO Preventer API. The legacy app
 * hardcoded the HBR one, `https://preventer-apim.azure-api.net/preventer-api/savemeta`.
 */
const DEFAULT_META_URL =
  'https://sy-vo-prd-preventer-apim.azure-api.net/sy-vo-prd-preventer-api/savemeta';

/**
 * Legacy retry delay, unchanged and deliberately infinite. A display whose ACL has not been
 * provisioned must keep asking until an operator provisions it; nobody is going to walk over
 * to a screen in another country and reload it.
 */
export const META_RETRY_MS = 60_000;

/** Backstop poll for the missed-`load` race. See `initPreventer`. */
const TOKEN_POLL_INTERVAL_MS = 500;
const TOKEN_POLL_TIMEOUT_MS = 30_000;

/* ---------------------------------------------------------------------------------------- */
/* Token acquisition                                                                          */
/* ---------------------------------------------------------------------------------------- */

export type InitPreventerOptions = {
  pollIntervalMs?: number;
  pollTimeoutMs?: number;
};

/**
 * Acquires a preventer token and hands it to `onToken` exactly once.
 *
 * Ported from `_preventerInit` plus the plugin body that wraps it. Three states, matching
 * the table in `types.ts`:
 *
 * 1. `window.preventer === undefined` — the script has not run. Attach a `load` listener to
 *    `#preventerScript` and re-evaluate when it fires.
 * 2. `window.preventer.token === undefined` — the script is up but has no token yet.
 *    **Replace the whole `window.preventer` object** with `{ tokenAcquiredCallback }`.
 * 3. otherwise — the token is already there; take it.
 *
 * ## Why case 2 replaces the object
 *
 * Assigning `window.preventer.tokenAcquiredCallback = fn` would be the obvious code and it
 * is *not* what the legacy app does. The external script reads its global back and expects
 * to find the callback on a fresh object; the replacement is the handshake. It looks like a
 * bug. It is load-bearing — changing it means the script never calls us and the display
 * never gets a token, with nothing in the UI to say so.
 *
 * ## The race the legacy code does not handle
 *
 * `preventer.js` is `async`, so it can finish loading *before* this function runs — the
 * listener is then attached to a script that has already fired `load`, it never runs, and
 * the display sits at a frozen screen forever. The legacy app survives this by luck of
 * timing. Here a `pollIntervalMs` poll re-evaluates the same three cases for
 * `pollTimeoutMs`, alongside the listener rather than instead of it, and logs which of the
 * two actually delivered so the race is measurable in the field rather than theoretical.
 *
 * @returns a cleanup that stops the poll, drops the listener, and neutralises a late
 *   callback. Called from `MqttProvider`'s effect cleanup — in production nothing unmounts,
 *   but StrictMode exercises this path on every dev boot.
 */
export function initPreventer(
  onToken: (token: string) => void,
  {
    pollIntervalMs = TOKEN_POLL_INTERVAL_MS,
    pollTimeoutMs = TOKEN_POLL_TIMEOUT_MS,
  }: InitPreventerOptions = {},
): () => void {
  let settled = false;
  let pollTimer: ReturnType<typeof setInterval> | undefined;
  let pollDeadline: ReturnType<typeof setTimeout> | undefined;

  const script = document.querySelector('#preventerScript');

  const stopPolling = () => {
    if (pollTimer !== undefined) {
      clearInterval(pollTimer);
      pollTimer = undefined;
    }
    if (pollDeadline !== undefined) {
      clearTimeout(pollDeadline);
      pollDeadline = undefined;
    }
  };

  const deliver = (token: unknown, via: string) => {
    if (settled) return;
    // The script can call us with no token at all — see `tokenAcquiredCallback` in types.ts.
    // Without this guard that arrives as `undefined.length` inside a callback the script
    // invokes from its own try/catch, which swallows the TypeError and reports it as
    // "Unable to connect API endpoint". Two failures, one misleading message, no token.
    if (typeof token !== 'string' || token === '') {
      console.error(
        '[preventer] %s produced no usable token (%o). This display is probably not ' +
          'registered with the preventer; it will not connect to MQTT.',
        via,
        token,
      );
      return;
    }
    settled = true;
    stopPolling();
    // Length only. The token *is* the MQTT credential pair; printing it would put a working
    // broker login into every remote console session.
    console.info('[preventer] token acquired via %s (%d chars)', via, token.length);
    onToken(token);
  };

  /**
   * Our side of the handshake, kept as one object so `step` can tell "already handed off"
   * from "the script replaced it" with an identity check instead of a heuristic.
   */
  const handoff: PreventerGlobal = {
    tokenAcquiredCallback: (token: string, fingerprint?: string, enabled?: boolean) => {
      if (enabled === false) {
        // The script is about to navigate this page to its registration flow. Say why first,
        // and name the fingerprint — that is the id an operator has to provision.
        console.error(
          '[preventer] this display is not enabled (fingerprint %s). The preventer script ' +
            'will now redirect to preventer-check.html; register the fingerprint to fix it.',
          fingerprint,
        );
      }
      deliver(token, 'tokenAcquiredCallback');
    },
  };

  /** `_preventerInit`, structure-for-structure. Safe to call any number of times. */
  const step = (via: string) => {
    if (settled) return;
    const preventer = window.preventer;
    if (preventer === undefined) return; // case 1: nothing to talk to yet
    if (preventer.token === undefined) {
      if (preventer === handoff) return; // case 2, already done
      window.preventer = handoff; // case 2: object replacement, see above
      console.info('[preventer] handed the script a tokenAcquiredCallback (via %s)', via);
      return;
    }
    deliver(preventer.token, via); // case 3
  };

  const onScriptLoad = () => {
    console.info('[preventer] #preventerScript fired load');
    step('script load');
  };

  if (window.preventer === undefined) {
    if (script === null) {
      console.warn(
        '[preventer] #preventerScript is not in the document — check index.html. Falling ' +
          'back to the poll backstop, which cannot help if the script is genuinely absent.',
      );
    } else {
      script.addEventListener('load', onScriptLoad, { once: true });
    }
  }

  // Armed *before* the first `step`, not after it: if the token is already there, `deliver`
  // calls `stopPolling` and both timers are gone again before anything can fire. That is one
  // branch fewer than asking "did the first attempt settle?", and it is the branch that
  // would decide whether a display ever recovers from the missed-`load` race.
  pollTimer = setInterval(() => {
    step('poll backstop');
  }, pollIntervalMs);
  pollDeadline = setTimeout(() => {
    stopPolling();
    if (settled) return;
    console.error(
      '[preventer] no token after %dms. The tokenAcquiredCallback stays armed, but until ' +
        'the preventer script delivers a token this display cannot connect to MQTT.',
      pollTimeoutMs,
    );
  }, pollTimeoutMs);

  step('init');

  return () => {
    settled = true;
    stopPolling();
    script?.removeEventListener('load', onScriptLoad);
  };
}

/* ---------------------------------------------------------------------------------------- */
/* Topic access check (`savemeta`)                                                            */
/* ---------------------------------------------------------------------------------------- */

export type BuildMetaTopicsInput = {
  /** Absolute topic keys — `topicItems.keys()`. Already `${baseTopic}/${topic}`. */
  topics: Iterable<string>;
  baseTopic: string;
  floorCode: string;
};

/**
 * Builds the `meta.topics` array, byte-for-byte as `_preperMeta` built it.
 *
 * The registered keys go in **unchanged** — `registerTopic` already prefixed them with
 * `baseTopic`, and prefixing again would ask the broker to authorise
 * `B0004/…/signage/B0004/…/signage/all/weather`, which it would grant happily and which
 * would then never match a subscribe.
 *
 * Order is registered topics, then `refresh`, then `heartbeat`, and it is preserved. The API
 * almost certainly does not care; "almost certainly" is not something that can be checked
 * against a production ACL service from a development machine.
 */
export function buildMetaTopics({
  topics,
  baseTopic,
  floorCode,
}: BuildMetaTopicsInput): MetaTopic[] {
  const meta: MetaTopic[] = [];
  for (const name of topics) {
    meta.push({ name, permission: 'read' });
  }
  // Remote reload channel — the only way to recover a wedged display.
  meta.push({ name: `${baseTopic}/refresh`, permission: 'read' });
  // The one topic the display *writes*. Note the floor segment sits between base and leaf.
  meta.push({ name: `${baseTopic}/${floorCode}/heartbeat`, permission: 'write' });
  return meta;
}

export type TopicAccessOptions = {
  token: string;
  topics: MetaTopic[];
  /** Called once, when the endpoint confirms the ACL is provisioned. */
  onGranted: () => void;
  metaUrl?: string;
  apiKey?: string;
  retryDelayMs?: number;
};

/**
 * Asks the preventer to provision the broker ACL for this display, retrying until it does.
 *
 * Ported from `_checkMeta` / `_fetchPReventerMeta`. Success is HTTP 200 with a truthy
 * `metaEnabled`; **everything else** — non-200, falsy `metaEnabled`, network failure,
 * unparseable JSON — schedules an identical retry `retryDelayMs` later, forever.
 *
 * @returns a cancel function. The legacy code used a bare `setTimeout` with no handle, so
 *   the retry chain outlived any conceivable teardown; here `MqttProvider` cancels it.
 */
export function startTopicAccessCheck({
  token,
  topics,
  onGranted,
  metaUrl = import.meta.env.VITE_PREVENTER_META_URL ?? DEFAULT_META_URL,
  apiKey = import.meta.env.VITE_PREVENTER_API ?? '',
  retryDelayMs = META_RETRY_MS,
}: TopicAccessOptions): () => void {
  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let attempt = 0;

  // Built once and reused verbatim, so a retry is provably the same request as the attempt
  // that failed.
  const body = JSON.stringify({ token, meta: { topics } });
  const topicList = topics.map((t) => `${t.permission.padEnd(5)} ${t.name}`).join('\n  ');

  if (!apiKey) {
    console.error(
      '[preventer] VITE_PREVENTER_API is empty; savemeta will be rejected and this display ' +
        'will retry every %dms forever. Check the build environment.',
      retryDelayMs,
    );
  }

  const run = async (): Promise<void> => {
    attempt += 1;
    // The topic list is the useful half of this request and the half that is not a secret.
    console.info(
      '[preventer] savemeta attempt %d — %d topics:\n  %s',
      attempt,
      topics.length,
      topicList,
    );

    let granted = false;
    try {
      const response = await fetch(metaUrl, {
        method: 'POST',
        body,
        // No `Content-Type` — deliberate, and load-bearing (docs/MIGRATION.md §6). A string
        // body with no content type is sent as `text/plain`, which keeps this a *simple*
        // CORS request. Adding `application/json` promotes it to a preflighted one, and the
        // endpoint may not answer `OPTIONS`. Do not add it without testing against the real
        // endpoint first.
        headers: { 'x-api-key': apiKey },
      });

      if (response.status === 200) {
        const data: unknown = await response.json();
        const metaEnabled = readMetaEnabled(data);
        granted = Boolean(metaEnabled);
        if (granted && metaEnabled !== true) {
          // Legacy checked `if (data.metaEnabled)`, i.e. truthiness, and that is what runs on
          // the wall today. Keeping truthiness means a `1` or a `"true"` from the API cannot
          // strand a display in an infinite retry loop; the warning is here so a change in
          // the endpoint's response shape is visible rather than merely tolerated.
          console.warn(
            '[preventer] metaEnabled is %o, not the boolean true — treated as granted for ' +
              'legacy parity',
            metaEnabled,
          );
        }
        if (!granted) {
          console.warn(
            '[preventer] savemeta returned metaEnabled=%o — the broker ACL is not provisioned ' +
              'for this display yet',
            metaEnabled,
          );
        }
      } else {
        console.warn('[preventer] savemeta HTTP %d %s', response.status, response.statusText);
      }
    } catch (error) {
      // Network failure, CORS rejection, or a body that is not JSON. All the same outcome.
      console.warn('[preventer] savemeta request failed', error);
    }

    if (cancelled) {
      console.info('[preventer] savemeta cancelled; discarding attempt %d', attempt);
      return;
    }

    if (granted) {
      console.info('[preventer] topic access granted after %d attempt(s)', attempt);
      onGranted();
      return;
    }

    console.info(
      '[preventer] retrying savemeta in %dms (attempt %d failed)',
      retryDelayMs,
      attempt,
    );
    timer = setTimeout(() => {
      void run();
    }, retryDelayMs);
  };

  void run();

  return () => {
    cancelled = true;
    if (timer !== undefined) {
      clearTimeout(timer);
      timer = undefined;
    }
  };
}

/** `data.metaEnabled` without trusting `data` to be an object at all. */
function readMetaEnabled(data: unknown): unknown {
  return typeof data === 'object' && data !== null && 'metaEnabled' in data
    ? data.metaEnabled
    : undefined;
}
